import React, { Component, ErrorInfo, ReactNode } from 'react';
import { capturePostHogException } from 'src/utils/posthog';

interface Props {
    children: ReactNode;
    fallback?: ReactNode | ((error: Error, info: ErrorInfo | null) => ReactNode);
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

export class OxErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        return {
            hasError: true,
            error
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        this.setState({
            error,
            errorInfo
        });

        try {
            const href = typeof window !== 'undefined' ? window.location.href : undefined;
            const safeUrl = href
                ? (() => {
                      try {
                          const u = new URL(href);
                          u.search = '';
                          u.hash = '';
                          return u.toString();
                      } catch {
                          return undefined;
                      }
                  })()
                : undefined;
            capturePostHogException(error, {
                component_stack: errorInfo.componentStack,
                error_boundary: 'OxErrorBoundary',
                error_name: error.name,
                error_message: error.message,
                stack_trace: (error.stack || '').slice(0, 8000),
                url: safeUrl
            });
        } catch {
            // no-op
        }
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                if (typeof this.props.fallback === 'function') {
                    // Try calling as render prop first, fall back to component if it fails
                    try {
                        return (this.props.fallback as (e: Error, i: ErrorInfo | null) => ReactNode)(
                            this.state.error || new Error('Unknown error'),
                            this.state.errorInfo
                        );
                    } catch {
                        // If calling with parameters fails, treat as React component
                        return React.createElement(this.props.fallback as React.ComponentType);
                    }
                }
                if (React.isValidElement(this.props.fallback)) {
                    return this.props.fallback;
                }
                return this.props.fallback as ReactNode;
            }

            return (
                <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    fontFamily: 'Arial, sans-serif'
                }}>
                    <h2>Something went wrong</h2>
                    <p>We&apos;ve been notified of this error and are working to fix it.</p>
                    <button 
                        onClick={() => window.location.reload()}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Refresh Page
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}